import React, { useState, useEffect, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  UserGroupIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  ClockIcon,
  TrophyIcon,
  ChartBarIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { getPlayersByTournament, supabase } from '../../../lib/supabase';
import { useTournament } from '../../TournamentContext';
import MobilePlayerRegistrationSheet from '../../components/MobilePlayerRegistrationSheet';
import MobilePlayerDetailsSheet from '../../components/MobilePlayerDetailsSheet';
import MobileAdminScores from './MobileAdminScores';
import MobilePlayerAnalytics from '../../components/MobilePlayerAnalytics';
import { toast } from 'react-hot-toast';

interface Player {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  resident_club: string;
  handicap: number;
  category: string;
  status: string;
  created_at: string;
  country: string;
  tournament_id: string;
  registration_status: { personalInfo: boolean; payment: boolean; verification: boolean };
  payment_proof_url?: string;
  shirt_size?: string;
  registration_number?: string;
  gender?: string;
}

interface FormattedPlayer {
  id: string;
  rowIndex: number;
  name: string;
  email: string;
  phone: string;
  club: string;
  handicap: number;
  category: string;
  status: string;
  registrationDate: string;
  paymentStatus: string;
  country: string;
  registrationStatus: { personalInfo: boolean; payment: boolean; verification: boolean };
  payment_proof_url?: string;
  shirt_size?: string;
  registration_number?: string;
  gender?: string;
}

function statusColor(status: string) {
  switch (status) {
    case 'Active': return 'bg-green-100 text-green-700';
    case 'Pending': return 'bg-amber-100 text-amber-700';
    case 'Withdrawn': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-600';
  }
}

type PlayerTab = 'players' | 'scores' | 'analytics';

export default function MobileAdminPlayers() {
  const { selectedTournament } = useTournament();
  const tournamentId = selectedTournament?.id || '';
  const [activeTab, setActiveTab] = useState<PlayerTab>('players');
  const [players, setPlayers] = useState<FormattedPlayer[]>([]);
  const [rawPlayers, setRawPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<FormattedPlayer | null>(null);
  const [showCapacity, setShowCapacity] = useState(false);
  const [reservedSpots, setReservedSpots] = useState(0);
  const [totalCapacity, setTotalCapacity] = useState(250);
  const [capacityLoading, setCapacityLoading] = useState(false);
  const [capacitySaving, setCapacitySaving] = useState(false);

  useEffect(() => {
    if (tournamentId) fetchPlayers();
  }, [tournamentId]);

  const fetchPlayers = async () => {
    try {
      setLoading(true);
      const { data, error } = await getPlayersByTournament(tournamentId);
      if (error) throw error;
      setRawPlayers(data || []);
      const formatted: FormattedPlayer[] = (data || []).map((p: Player, i: number) => ({
        id: p.id,
        rowIndex: i + 1,
        name: `${p.first_name} ${p.last_name}`,
        email: p.email,
        phone: p.phone,
        club: p.resident_club,
        handicap: p.handicap,
        category: p.category,
        status: p.status,
        registrationDate: new Date(p.created_at).toLocaleDateString(),
        paymentStatus: p.registration_status?.payment ? 'Paid' : 'Pending',
        country: p.country,
        registrationStatus: p.registration_status,
        payment_proof_url: p.payment_proof_url,
        shirt_size: p.shirt_size,
        registration_number: p.registration_number,
        gender: p.gender
      }));
      setPlayers(formatted);
    } catch {
      toast.error('Failed to load players');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => players.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.email.toLowerCase().includes(search.toLowerCase()) ||
      p.club.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'All' || p.status === statusFilter;
    return matchSearch && matchStatus;
  }), [players, search, statusFilter]);

  const stats = useMemo(() => ({
    total: players.length,
    active: players.filter(p => p.status?.toLowerCase() === 'active').length,
    pending: players.filter(p => p.status?.toLowerCase() === 'pending').length,
    international: players.filter(p => p.country && p.country.toLowerCase() !== 'nigeria').length,
  }), [players]);

  const fetchCapacityData = async () => {
    try {
      setCapacityLoading(true);
      const { data: settingsData } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'reserved_spots')
        .maybeSingle();
      if (settingsData) {
        const v = settingsData.value;
        const parsed = typeof v === 'number' ? v : parseInt(String(v));
        setReservedSpots(isNaN(parsed) ? 0 : parsed);
      }
      const { data: tournamentData } = await supabase
        .from('tournaments')
        .select('capacity')
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true })
        .limit(1)
        .maybeSingle();
      setTotalCapacity(tournamentData?.capacity ?? 250);
    } catch {
      toast.error('Failed to load capacity data');
    } finally {
      setCapacityLoading(false);
    }
  };

  const handleToggleCapacity = () => {
    if (!showCapacity) fetchCapacityData();
    setShowCapacity(v => !v);
  };

  const handleSaveCapacity = async () => {
    setCapacitySaving(true);
    try {
      const { data: existing } = await supabase
        .from('system_settings')
        .select('id')
        .eq('key', 'reserved_spots')
        .maybeSingle();
      if (existing) {
        await supabase
          .from('system_settings')
          .update({ value: reservedSpots, updated_at: new Date().toISOString() })
          .eq('id', existing.id);
      } else {
        await supabase.from('system_settings').insert([{
          key: 'reserved_spots',
          value: reservedSpots,
          category: 'registration',
          description: 'Number of spots reserved for special registrations',
          is_public: true,
        }]);
      }
      toast.success('Reserved spots updated');
    } catch {
      toast.error('Failed to update reserved spots');
    } finally {
      setCapacitySaving(false);
    }
  };

  const tabBar = (
    <div className="flex gap-1 px-4 pt-4 pb-2">
      {([
        { key: 'players', label: 'Players', icon: UserGroupIcon },
        { key: 'scores', label: 'Scores', icon: TrophyIcon },
        { key: 'analytics', label: 'Analytics', icon: ChartBarIcon },
      ] as { key: PlayerTab; label: string; icon: React.ElementType }[]).map(tab => (
        <button
          key={tab.key}
          onClick={() => setActiveTab(tab.key)}
          className={`flex-1 flex items-center justify-center gap-1 py-2.5 rounded-xl text-xs font-medium transition-colors ${
            activeTab === tab.key ? 'bg-golf-orange text-white' : 'bg-gray-100 text-gray-600'
          }`}
        >
          <tab.icon className="w-3.5 h-3.5" />
          {tab.label}
        </button>
      ))}
    </div>
  );

  if (activeTab === 'scores') {
    return (
      <div>
        {tabBar}
        <MobileAdminScores />
      </div>
    );
  }

  if (activeTab === 'analytics') {
    return (
      <div className="pb-4 space-y-4">
        {tabBar}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
          </div>
        ) : (
          <MobilePlayerAnalytics players={rawPlayers} />
        )}
      </div>
    );
  }

  return (
    <div className="pb-4 space-y-4">
      {/* Tab toggle */}
      {tabBar}

      {/* Stats */}
      <div className="px-4 grid grid-cols-2 gap-3">
        {[
          { label: 'Total', value: stats.total, icon: UserGroupIcon, color: 'text-blue-500 bg-blue-50' },
          { label: 'Active', value: stats.active, icon: CheckCircleIcon, color: 'text-green-600 bg-green-50' },
          { label: 'Pending', value: stats.pending, icon: ClockIcon, color: 'text-amber-600 bg-amber-50' },
          { label: 'International', value: stats.international, icon: GlobeAltIcon, color: 'text-golf-orange bg-golf-orange/10' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${s.color}`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className="text-lg font-bold text-golf-gray">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Registration Capacity Management */}
      <div className="px-4">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-golf-gray">Registration Capacity Management</p>
              <p className="text-xs text-gray-400 mt-0.5">Manage reserved spots and player registration capacity</p>
            </div>
            <button
              onClick={handleToggleCapacity}
              className="flex-shrink-0 px-3 py-1.5 bg-golf-green text-white text-xs font-medium rounded-lg hover:bg-golf-green/90 transition-colors"
            >
              {showCapacity ? 'Hide Manager' : 'Show Manager'}
            </button>
          </div>

          {showCapacity && (
            <div className="px-4 pb-4 border-t border-gray-100 pt-4 space-y-4">
              {capacityLoading ? (
                <div className="flex justify-center py-6">
                  <div className="w-5 h-5 border-2 border-golf-orange border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (() => {
                const currentRegistrations = stats.total;
                const availableSpots = totalCapacity - reservedSpots - currentRegistrations;
                const registeredPct = (currentRegistrations / totalCapacity) * 100;
                const reservedPct = (reservedSpots / totalCapacity) * 100;
                const availablePct = Math.max(0, 100 - registeredPct - reservedPct);
                const maxReserved = Math.max(0, totalCapacity - currentRegistrations);
                return (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: 'Total', value: totalCapacity },
                        { label: 'Registered', value: currentRegistrations },
                        { label: 'Available', value: availableSpots },
                      ].map(s => (
                        <div key={s.label} className="bg-gray-50 rounded-xl p-3 text-center">
                          <p className="text-lg font-bold text-golf-gray">{s.value}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
                        </div>
                      ))}
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-medium text-gray-700">Reserved Spots</p>
                        <span className="text-xs font-semibold text-golf-orange">{reservedSpots}</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={maxReserved}
                        value={reservedSpots}
                        onChange={e => setReservedSpots(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-golf-orange"
                      />
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>0</span>
                        <span>{maxReserved}</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <input
                        type="number"
                        min={0}
                        max={maxReserved}
                        value={reservedSpots}
                        onChange={e => {
                          const v = parseInt(e.target.value);
                          if (!isNaN(v) && v >= 0 && v <= maxReserved) setReservedSpots(v);
                        }}
                        className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-golf-orange"
                      />
                      <button
                        onClick={handleSaveCapacity}
                        disabled={capacitySaving}
                        className="px-4 py-2 bg-golf-orange text-white text-sm font-medium rounded-xl hover:bg-golf-orange/90 transition-colors disabled:opacity-50"
                      >
                        {capacitySaving ? 'Saving…' : 'Save'}
                      </button>
                    </div>

                    <div>
                      <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden flex">
                        <div className="bg-green-500 h-full transition-all" style={{ width: `${registeredPct}%` }} />
                        <div className="bg-gray-400 h-full transition-all" style={{ width: `${reservedPct}%` }} />
                        <div className="bg-blue-400 h-full transition-all" style={{ width: `${availablePct}%` }} />
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                        {[
                          { color: 'bg-green-500', label: `Registered (${currentRegistrations})` },
                          { color: 'bg-gray-400', label: `Reserved (${reservedSpots})` },
                          { color: 'bg-blue-400', label: `Available (${availableSpots})` },
                        ].map(item => (
                          <div key={item.label} className="flex items-center gap-1.5">
                            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${item.color}`} />
                            <span className="text-xs text-gray-500">{item.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-start gap-2">
                      <InformationCircleIcon className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-blue-700 leading-relaxed">
                        Reserved spots are held for VIP players, sponsors, or officials and are not available for general registration.
                      </p>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Sticky Search + Filters */}
      <div className="sticky top-14 z-10 bg-gray-50 px-4 pt-2 pb-3 space-y-2 border-b border-gray-100">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search players..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-golf-orange bg-white"
            />
          </div>
          <button
            onClick={() => setIsAddOpen(true)}
            className="bg-golf-orange text-white rounded-xl px-3 py-2.5 flex items-center gap-1.5 text-sm font-medium"
          >
            <PlusIcon className="w-4 h-4" />
            Add
          </button>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-0.5 scrollbar-hide">
          {['All', 'Active', 'Pending', 'Withdrawn'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                statusFilter === s
                  ? 'bg-golf-orange text-white border-golf-orange'
                  : 'bg-white text-gray-600 border-gray-200'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Player list */}
      {loading ? (
        <div className="px-4 flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="px-4 text-center py-12 text-gray-400 text-sm">No players found</div>
      ) : (
        <div className="px-4 space-y-2">
          {filtered.map(player => (
            <button
              key={player.id}
              onClick={() => setSelectedPlayer(player)}
              className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-4 text-left active:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-golf-gray text-sm truncate">{player.name}</p>
                  <p className="text-xs text-gray-500 truncate">{player.club}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs text-gray-400">{player.category}</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-xs text-gray-400">HCP {player.handicap}</span>
                    <span className="text-gray-300">·</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${player.paymentStatus === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {player.paymentStatus}
                    </span>
                  </div>
                </div>
                <span className={`flex-shrink-0 text-xs px-2 py-1 rounded-full font-medium ${statusColor(player.status)}`}>
                  {player.status}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      <MobilePlayerRegistrationSheet
        isOpen={isAddOpen}
        onClose={() => { setIsAddOpen(false); fetchPlayers(); }}
      />

      {selectedPlayer && (
        <MobilePlayerDetailsSheet
          isOpen={!!selectedPlayer}
          onClose={() => { setSelectedPlayer(null); fetchPlayers(); }}
          player={selectedPlayer}
          tournamentId={tournamentId}
        />
      )}
    </div>
  );
}
